import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { HOME, SOLUTIONS_PAGES } from "@/lib/content";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { ArrowRight, Scale, HardHat, Umbrella, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";

const industryIconMap: Record<string, React.ElementType> = {
  Legal: Scale,
  Construction: HardHat,
  Insurance: Umbrella,
};

export default function SolutionsOverviewPage() {
  const enterprise = SOLUTIONS_PAGES.enterprise;

  return (
    <Layout>
      <SEOHead
        title="Solutions — Legal, Construction, and Insurance | ClauseArk"
        description="ClauseArk is a contract intelligence and lifecycle platform for contract teams. Three industry packs — Legal (in-house and law firms), Construction, and Insurance — share the same product, with side-aware playbooks tuned to each."
      />

      {/* Hero */}
      <section className="pt-24 pb-16 md:pt-32 md:pb-20 border-b border-border/50 bg-slate-50 dark:bg-navy/30">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              Solutions
            </div>
            <h1 className="text-display mb-6 text-navy dark:text-white">
              {HOME.industries.headline}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              {HOME.industries.sub}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Three industries */}
      <section className="py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {HOME.industries.cards.map((card, i) => {
              const Icon = industryIconMap[card.industry] ?? Scale;
              return (
                <motion.div
                  key={card.industry}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.45, delay: i * 0.08 }}
                  className="bg-white dark:bg-navy/60 rounded-2xl p-8 border border-mist dark:border-white/10 hover:border-indigo/40 hover:shadow-lg transition-all flex flex-col gap-5"
                  data-testid={`solutions-overview-card-${card.industry.toLowerCase()}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-indigo-50 text-indigo flex items-center justify-center">
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="text-h3 text-navy dark:text-white mb-3">
                      {card.industry}
                    </h2>
                    <p className="text-body text-slate-600 dark:text-slate-400 leading-relaxed">
                      {card.body}
                    </p>
                  </div>
                  <div className="mt-auto flex flex-col gap-2 pt-2">
                    {card.links.map((link) => (
                      <Link
                        key={link.path}
                        href={link.path}
                        className="inline-flex items-center justify-between gap-2 text-sm font-semibold text-indigo hover:text-navy dark:hover:text-white transition-colors group border border-mist dark:border-white/10 rounded-lg px-4 py-2.5"
                        data-testid={`solutions-overview-link-${link.path.split("/").pop()}`}
                      >
                        <span>{link.label}</span>
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Enterprise (security & SSO) — buyer shape, peer-secondary */}
      <section
        className="py-16 md:py-20 bg-slate-50 dark:bg-navy/30 border-t border-border/50"
        data-testid="solutions-overview-enterprise"
      >
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto bg-white dark:bg-navy/60 border border-mist dark:border-white/10 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row gap-6 items-start">
            <div className="h-12 w-12 rounded-xl bg-cyan-50 text-cyan flex items-center justify-center shrink-0">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.2em] text-indigo font-semibold mb-2">
                {enterprise.eyebrow}
              </div>
              <h2 className="text-h3 text-navy dark:text-white mb-3">
                {enterprise.title}
              </h2>
              <p className="text-body text-slate-600 dark:text-slate-400 leading-relaxed mb-5">
                {enterprise.subtitle}
              </p>
              <Link
                href="/solutions/enterprise"
                className="inline-flex items-center gap-2 text-sm font-semibold text-indigo hover:text-navy dark:hover:text-white transition-colors group"
                data-testid="solutions-overview-link-enterprise"
              >
                See Enterprise (security &amp; SSO)
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-brand-gradient text-white">
        <div className="container mx-auto px-4 md:px-6 text-center max-w-3xl">
          <h2 className="text-h2 text-white mb-4">
            One product. Three industry packs.
          </h2>
          <p className="text-body-lg text-white/90 mb-8">
            Pick the pack that matches your contracts. Same review, same lifecycle, same Word add-in — tuned to how your industry actually negotiates paper.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link href="/demo">
              <Button
                size="lg"
                className="w-full sm:w-auto h-12 px-7 bg-white text-indigo hover:bg-slate-100 transition-colors text-base font-semibold no-default-hover-elevate"
                data-testid="solutions-overview-cta-demo"
              >
                Book a demo
              </Button>
            </Link>
            <Link href="/pricing">
              <Button
                size="lg"
                variant="outline"
                className="w-full sm:w-auto h-12 px-7 border-white/40 bg-transparent text-white hover:bg-white/10 text-base font-semibold no-default-hover-elevate"
                data-testid="solutions-overview-cta-pricing"
              >
                See pricing
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
